"use client"

import { useState } from "react"
import DataError from "@ui/data-error"
import { Button, Skeleton } from "@ui/shad"
import { format } from "date-fns"
import { History } from "lucide-react"

import { useFetchMeetingVersion, useInfiniteMeetingVersions } from "@/hooks/api/meetings/versions/queries"
import { FullscreenDialog } from "@/components/dialogs"
import { Editor } from "@/components/lexical"

interface MeetingVersionDialogProps {
  meetingId: string
  versionId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MeetingVersionDialog({ meetingId, versionId, open, onOpenChange }: MeetingVersionDialogProps) {
  return (
    <FullscreenDialog title="Meeting notepad history" open={open} onOpenChange={onOpenChange}>
      {open && <MeetingVersionHistory key={`${meetingId}-${versionId}`} meetingId={meetingId} versionId={versionId} />}
    </FullscreenDialog>
  )
}

export function MeetingNotepadHistoryButton({ meetingId }: { meetingId: string }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <History />
        Version history
      </Button>
      <MeetingVersionDialog meetingId={meetingId} open={open} onOpenChange={setOpen} />
    </>
  )
}

function MeetingVersionHistory({ meetingId, versionId }: { meetingId: string; versionId?: string }) {
  const [selectedVersionId, setSelectedVersionId] = useState(versionId)
  const versions = useInfiniteMeetingVersions(meetingId)
  const activeVersionId = selectedVersionId ?? versions.data?.pages[0]?.versions[0]?.id
  const snapshot = useFetchMeetingVersion(meetingId, activeVersionId)

  return (
    <div className="grid min-h-0 gap-4 md:grid-cols-[16rem_minmax(0,1fr)]">
      <aside className="space-y-2">
        {versions.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : versions.isError ? (
          <DataError
            title="Unable to load versions"
            action={<Button onClick={() => versions.refetch()}>Try again</Button>}
          />
        ) : (
          <>
            {versions.data.pages
              .flatMap((page) => page.versions)
              .map((version) => (
                <button
                  key={version.id}
                  type="button"
                  aria-pressed={activeVersionId === version.id}
                  className="hover:bg-muted aria-pressed:bg-muted block w-full rounded-md border p-3 text-left"
                  onClick={() => setSelectedVersionId(version.id)}
                >
                  <span className="block text-sm">
                    {format(new Date(version.created_at), "MMM d, yyyy · h:mm:ss a")}
                  </span>
                  <span className="text-muted-foreground text-xs">{version.actor?.name ?? "System"}</span>
                </button>
              ))}
            {versions.hasNextPage && (
              <Button
                variant="outline"
                size="sm"
                disabled={versions.isFetchingNextPage}
                onClick={() => versions.fetchNextPage()}
              >
                Load more
              </Button>
            )}
          </>
        )}
      </aside>
      <div className="min-w-0 space-y-4">
        {versions.isError ? null : !versions.isPending && !activeVersionId ? (
          <p className="text-muted-foreground text-sm">No saved versions.</p>
        ) : snapshot.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : snapshot.isError ? (
          <DataError
            title="Unable to load this version"
            action={<Button onClick={() => snapshot.refetch()}>Try again</Button>}
          />
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {format(new Date(snapshot.data.created_at), "MMM d, yyyy · h:mm:ss a")} ·{" "}
              {snapshot.data.actor?.name ?? "System"}
            </p>
            <Editor key={selectedVersionId} initialContent={snapshot.data.notepad} editable={false} placeholder="" />
          </>
        )}
      </div>
    </div>
  )
}
